import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/CartContext";
import { buildWhatsAppLink } from "../data/menu";

const formatPrice = (v) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const CartDrawer = () => {
  const { items, isCartOpen, setIsCartOpen, updateQuantity, removeItem, totalPrice, totalItems } = useCart();

  const handleCheckout = () => {
    let msg = "Olá Santo Nhoque! Gostaria de fazer o seguinte pedido:\n\n";
    items.forEach((item) => {
      msg += `▪ ${item.quantity}x ${item.name} (${item.size}) - ${formatPrice(item.price * item.quantity)}\n`;
    });
    msg += `\n*Total estimado: ${formatPrice(totalPrice)}*`;
    
    window.open(buildWhatsAppLink(msg), "_blank");
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsCartOpen(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[90]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full sm:w-[400px] bg-[var(--sn-bg-deep)] border-l border-[var(--sn-gold)]/30 z-[100] flex flex-col shadow-2xl sn-grain"
          >
            <div className="flex items-center justify-between p-6 border-b border-[var(--sn-border)]">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-[var(--sn-gold)]" />
                <h2 className="font-display text-2xl text-[var(--sn-cream)]">Seu Pedido</h2>
              </div>
              <button
                onClick={() => setIsCartOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[var(--sn-gold)]/10 text-[var(--sn-gold)] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-[var(--sn-cream-soft)]/60 space-y-4">
                  <ShoppingBag className="w-16 h-16 opacity-20" />
                  <p>Seu carrinho está vazio.</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    className="text-[var(--sn-gold)] border border-[var(--sn-gold)] px-6 py-2 rounded-md hover:bg-[var(--sn-gold)]/10 transition-colors mt-4"
                  >
                    Ver Cardápio
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => (
                    <div key={`${item.id}-${item.size}`} className="flex gap-4 border-b border-[var(--sn-border)] pb-4">
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-display text-lg text-[var(--sn-cream)]">{item.name}</h4>
                          <button 
                            onClick={() => removeItem(item.id, item.size)}
                            className="text-red-400/70 hover:text-red-400 p-1 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-[10px] uppercase tracking-widest text-[var(--sn-gold)]/70 mb-3">
                          {item.size} — {formatPrice(item.price)}
                        </p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 bg-[var(--sn-bg-surface)] border border-[var(--sn-border)] rounded-md px-2 py-1">
                            <button 
                              onClick={() => updateQuantity(item.id, item.size, -1)}
                              className="text-[var(--sn-gold)] hover:text-white p-1"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-[var(--sn-cream)] text-sm w-4 text-center">{item.quantity}</span>
                            <button 
                              onClick={() => updateQuantity(item.id, item.size, 1)}
                              className="text-[var(--sn-gold)] hover:text-white p-1"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <p className="font-display text-[var(--sn-cream)]">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-[var(--sn-border)] bg-[var(--sn-bg-surface)]/30">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-[var(--sn-cream-soft)]">Total estimado:</span>
                  <span className="font-display text-2xl text-[var(--sn-gold)]">{formatPrice(totalPrice)}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  className="w-full flex items-center justify-center gap-2 bg-[var(--sn-whatsapp)] hover:bg-[var(--sn-whatsapp-hover)] text-white font-medium px-6 py-4 rounded-md transition-all shadow-lg shadow-[var(--sn-whatsapp)]/20"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M20.52 3.48A11.85 11.85 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 22a9.93 9.93 0 0 1-5.07-1.39l-.36-.21-3.67.96.98-3.58-.23-.37A9.96 9.96 0 1 1 22 12c0 5.51-4.49 10-10 10Zm5.49-7.46c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.27-.47-2.42-1.5a9.05 9.05 0 0 1-1.67-2.07c-.18-.3-.02-.46.13-.62.14-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.2-.24-.58-.49-.5-.66-.5l-.56-.01c-.2 0-.52.08-.79.37-.27.3-1.03 1-1.03 2.45s1.06 2.85 1.21 3.05c.15.2 2.08 3.17 5.04 4.45.7.3 1.25.48 1.68.62.7.22 1.34.2 1.85.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
                  </svg>
                  Enviar Pedido
                </button>
                <p className="text-[10px] text-center text-[var(--sn-cream-soft)]/50 mt-4 uppercase tracking-widest">
                  O pagamento é combinado via WhatsApp
                </p>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
